import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Home, Building, MapPin, FileText, ChevronRight,
  ChevronLeft, Check, Info, AlertTriangle, Sparkles, Phone
} from "lucide-react";
import Navbar from "../../components/Header";
import axios from "axios";
// 🆕 source unique partagée (même fichier que EquipmentsStep / EquipmentsManager)
import { CATEGORIES, SUGGESTED_EQUIPMENTS } from "../../adminHotel/constants/equipments";

const G       = "var(--k-green)";
const G_DARK  = "var(--k-green-hover)";
const G_LIGHT = "var(--k-green-soft)";
const INK     = "#0f1d12";   // 🆕 vert très sombre pour les titres (plus éditorial que noir pur)
const API     = "https://api.komorotel.com";

const STEPS = [
  { Icon: Building2, label: "Établissement" },
  { Icon: MapPin,    label: "Coordonnées"   },
  { Icon: FileText,  label: "Description"   },
  { Icon: Sparkles,  label: "Équipements"   },
  { Icon: Check,     label: "Confirmation"  },
];

const TYPES = [
  { value: "hotel",              label: "Hôtel",                Icon: Building2, desc: "Établissement avec réception et services" },
  { value: "chambre_hote",       label: "Chambre d'hôte",       Icon: Home,      desc: "Chez l'habitant, accueil personnalisé" },
  { value: "appartement_studio", label: "Appartement / Studio", Icon: Building,  desc: "Logement indépendant et équipé" },
];

const ISLANDS = [
  { value: "Ngazidja", label: "Ngazidja", sub: "Grande Comore" },
  { value: "Anjouan",  label: "Anjouan",  sub: "Ndzuwani"   },
  { value: "Moheli",   label: "Mohéli",   sub: "Mwali"        },
];

// 🆕 styles responsive injectés une seule fois (pas de framework CSS sur cette page)
const RESPONSIVE_CSS = `
  .rh-shell { max-width: 820px; margin: 0 auto; padding: 44px 32px 72px; }
  .rh-hero-pad { padding-top: 92px; padding-bottom: 52px; }
  .rh-hero-inner { max-width: 800px; margin: 0 auto; padding: 0 32px; text-align: center; }
  .rh-hero-title { font-size: 38px; }
  .rh-card { padding: 34px; }
  .rh-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .rh-islands { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .rh-equip { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .rh-recap { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .rh-steplabel { display: inline; }

  @media (max-width: 640px) {
    .rh-shell { padding: 28px 18px 56px; }
    .rh-hero-pad { padding-top: 80px; padding-bottom: 40px; }
    .rh-hero-inner { padding: 0 20px; }
    .rh-hero-title { font-size: 27px; }
    .rh-card { padding: 22px 18px; }
    .rh-grid-2 { grid-template-columns: 1fr; }
    .rh-islands { grid-template-columns: 1fr; }
    .rh-equip { grid-template-columns: 1fr; }
    .rh-recap { grid-template-columns: 1fr; }
    .rh-steplabel { display: none; }   /* libellés d'étapes masqués, on garde les pastilles */
  }
`;

/* ── Champs ── */
function Field({ label, error, icon: Icon, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 7 }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        {Icon && (
          <Icon size={15} color={focused ? G : "#9ca3af"}
            style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", transition: "color .2s" }} />
        )}
        <input
          {...props}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%", border: `1.5px solid ${error ? "#ef4444" : focused ? G : "#e5e7eb"}`,
            borderRadius: 12, padding: Icon ? "12px 14px 12px 38px" : "12px 14px",
            fontSize: 14, outline: "none", fontFamily: "inherit", color: INK, background: "#fff",
            boxSizing: "border-box", transition: "border-color .2s",
          }}
        />
      </div>
      {error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Area({ label, error, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 7 }}>
          {label}
        </label>
      )}
      <textarea
        {...props}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%", border: `1.5px solid ${error ? "#ef4444" : focused ? G : "#e5e7eb"}`,
          borderRadius: 12, padding: "12px 14px", fontSize: 14, outline: "none",
          fontFamily: "inherit", color: INK, background: "#fff",
          boxSizing: "border-box", transition: "border-color .2s", resize: "vertical", lineHeight: 1.65,
        }}
      />
      {error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

/* ── Barre d'étapes ── */
function StepBar({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 38 }}>
      {STEPS.map((step, i) => {
        const done = i < current, active = i === current;
        const { Icon } = step;
        return (
          <React.Fragment key={i}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, minWidth: 34 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: done || active ? G : "#fff",
                border: `1.5px solid ${done || active ? G : "#e5e7eb"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: done || active ? "#fff" : "#9ca3af", transition: "all .3s",
                boxShadow: active ? `0 0 0 4px ${G_LIGHT}` : "none",
              }}>
                {done ? <Check size={15} strokeWidth={2.6} /> : <Icon size={15} />}
              </div>
              <span className="rh-steplabel" style={{
                fontSize: 11, fontWeight: active ? 700 : 500,
                color: active ? G : done ? "#374151" : "#9ca3af", whiteSpace: "nowrap",
              }}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1.5, background: done ? G : "#e5e7eb", marginTop: 17.5, transition: "background .3s" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const cardStyle = {
  background: "#fff", borderRadius: 18, border: "1px solid #eef0f1",
  boxShadow: "0 1px 2px rgba(16,29,18,.04), 0 8px 24px rgba(16,29,18,.04)",
};
const h2Style = { fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 21, fontWeight: 700, color: INK, marginBottom: 6, letterSpacing: "-0.01em" };
const subStyle = { fontSize: 13, color: "#8a9290", marginBottom: 24, lineHeight: 1.6 };

/* ─────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────── */
export default function RegisterHotel() {
  const navigate = useNavigate();
  const [step, setStep]       = useState(0);
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    type: "", island: "", name: "", city: "", adress: "",
    nif: "", email: "", phone: "", desc: "",
    equipments: [], // 🆕 [{ name, category }]
  });

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: "" }));
  };

  // 🆕 toggle au format {name, category} — cohérent avec le model Hotel et EquipmentsManager
  const isEquip = (name) => form.equipments.some(e => e.name === name);
  const toggleEquip = (name, category) => {
    setForm(p => ({
      ...p,
      equipments: isEquip(name)
        ? p.equipments.filter(e => e.name !== name)
        : [...p.equipments, { name, category }],
    }));
  };

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.type)   e.type   = "Sélectionnez un type";
      if (!form.island) e.island = "Sélectionnez une île";
    }
    if (step === 1) {
      if (!form.name.trim())   e.name   = "Requis";
      if (!form.city.trim())   e.city   = "Requis";
      if (!form.adress.trim()) e.adress = "Requis";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Email invalide";
      if (!form.phone.trim())  e.phone  = "Requis"; // 🆕 téléphone obligatoire (exigé par le backend)
    }
    if (step === 2) {
      if (!form.desc.trim() || form.desc.length < 50) e.desc = "Description trop courte (50 caractères min.)";
    }
    return e;
  };

  const next = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };
  const prev = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  /* ── Submit → crée une DEMANDE (HotelRequest), validée ensuite par le super admin ── */
  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});
    try {
      // 🆕 bon endpoint + bon mapping de champs (name→hotelName, desc→description)
      await axios.post(`${API}/api/superadmin/hotel-requests/public`, {
        hotelName:   form.name,
        type:        form.type,
        island:      form.island,
        city:        form.city,
        adress:      form.adress,
        email:       form.email,
        phone:       form.phone,
        description: form.desc,
        equipments:  form.equipments, // 🆕 [{name, category}]
        nif:         form.nif || undefined,
      });
      setStep(4);
      window.scrollTo(0, 0);
    } catch (err) {
      setErrors({ api: err.response?.data?.message || "Une erreur est survenue. Réessayez." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fbfcfb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>
      <style>{RESPONSIVE_CSS}</style>
      <Navbar />

      {/* Hero */}
      <div className="rh-hero-pad" style={{ background: `linear-gradient(160deg, #07200d 0%, ${INK} 55%, #0d3318 100%)` }}>
        <div className="rh-hero-inner">
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: "rgba(58,157,35,.16)", border: "1px solid rgba(58,157,35,.35)",
            color: "#9fd98a", fontSize: 11, fontWeight: 700, letterSpacing: ".1em",
            padding: "5px 13px", borderRadius: 100, marginBottom: 20, textTransform: "uppercase",
          }}>
            <Building2 size={12} /> Espace hôteliers
          </div>
          <h1 className="rh-hero-title" style={{
            fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 700,
            color: "#fff", lineHeight: 1.18, marginBottom: 14, letterSpacing: "-0.02em",
          }}>
            Référencez votre établissement
          </h1>
          <p style={{ fontSize: 14.5, color: "rgba(255,255,255,.66)", lineHeight: 1.7, maxWidth: 460, margin: "0 auto" }}>
            Rejoignez Komorotel et accueillez les voyageurs aux Comores.
            Commission de 8 % uniquement sur les réservations confirmées.
          </p>
        </div>
      </div>

      <div className="rh-shell">
        {step < 4 && <StepBar current={step} />}

        {/* ── ÉTAPE 0 — Type & Île ── */}
        {step === 0 && (
          <div className="rh-card" style={cardStyle}>
            <h2 style={h2Style}>Type d'établissement</h2>
            <p style={subStyle}>Quel type d'hébergement proposez-vous ?</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 11, marginBottom: 30 }}>
              {TYPES.map(t => {
                const sel = form.type === t.value; const { Icon } = t;
                return (
                  <div key={t.value} onClick={() => set("type", t.value)}
                    style={{
                      display: "flex", alignItems: "center", gap: 15, padding: "15px 18px",
                      border: `1.5px solid ${sel ? G : "#eef0f1"}`, borderRadius: 14, cursor: "pointer",
                      background: sel ? G_LIGHT : "#fff", transition: "all .18s",
                    }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                      background: sel ? G : "#f5f6f5", color: sel ? "#fff" : "#6b7280",
                      display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s",
                    }}>
                      <Icon size={19} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: INK }}>{t.label}</div>
                      <div style={{ fontSize: 12.5, color: "#8a9290", marginTop: 2 }}>{t.desc}</div>
                    </div>
                    <div style={{
                      width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                      border: `2px solid ${sel ? G : "#d1d5db"}`, background: sel ? G : "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s",
                    }}>
                      {sel && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
                    </div>
                  </div>
                );
              })}
            </div>
            {errors.type && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 18 }}>{errors.type}</div>}

            <h2 style={h2Style}>Île</h2>
            <p style={subStyle}>Où se situe votre établissement ?</p>

            <div className="rh-islands">
              {ISLANDS.map(isl => {
                const sel = form.island === isl.value;
                return (
                  <div key={isl.value} onClick={() => set("island", isl.value)}
                    style={{
                      padding: "16px 14px", border: `1.5px solid ${sel ? G : "#eef0f1"}`,
                      borderRadius: 14, cursor: "pointer", textAlign: "center",
                      background: sel ? G_LIGHT : "#fff", transition: "all .18s",
                    }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: sel ? G : INK }}>{isl.label}</div>
                    <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 3 }}>{isl.sub}</div>
                  </div>
                );
              })}
            </div>
            {errors.island && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 10 }}>{errors.island}</div>}
          </div>
        )}

        {/* ── ÉTAPE 1 — Coordonnées ── */}
        {step === 1 && (
          <div className="rh-card" style={cardStyle}>
            <h2 style={h2Style}>Informations générales</h2>
            <p style={subStyle}>Les informations de base sur votre établissement.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label="Nom de l'établissement *" value={form.name}
                onChange={e => set("name", e.target.value)} placeholder="ex : Hôtel Itsandra Beach" error={errors.name} />
              <div className="rh-grid-2">
                <Field label="Ville *" value={form.city} icon={MapPin}
                  onChange={e => set("city", e.target.value)} placeholder="ex : Moroni" error={errors.city} />
                <Field label="NIF (optionnel)" value={form.nif}
                  onChange={e => set("nif", e.target.value)} placeholder="Numéro fiscal" />
              </div>
              <Field label="Adresse complète *" value={form.adress}
                onChange={e => set("adress", e.target.value)} placeholder="Rue, quartier…" error={errors.adress} />
              <div className="rh-grid-2">
                <Field label="Email de contact *" type="email" value={form.email}
                  onChange={e => set("email", e.target.value)} placeholder="contact@monhotel.com" error={errors.email} />
                <Field label="Téléphone *" type="tel" value={form.phone} icon={Phone}
                  onChange={e => set("phone", e.target.value)} placeholder="+269 …" error={errors.phone} />
              </div>
            </div>

            <div style={{ marginTop: 20, background: "#f7f8f7", borderRadius: 12, padding: "13px 16px", display: "flex", gap: 10 }}>
              <Info size={15} color="#9ca3af" style={{ marginTop: 1, flexShrink: 0 }} />
              <p style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6, margin: 0 }}>
                Ces informations serviront à créer votre fiche. Votre email et votre téléphone ne sont jamais affichés publiquement.
              </p>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 2 — Description ── */}
        {step === 2 && (
          <div className="rh-card" style={cardStyle}>
            <h2 style={h2Style}>Description</h2>
            <p style={subStyle}>Présentez votre établissement aux voyageurs. Soyez précis sur ce qui vous rend unique.</p>

            <Area label="Description de l'établissement *" value={form.desc}
              onChange={e => set("desc", e.target.value)} rows={8}
              placeholder="Cadre, services, ambiance, points forts, environnement proche…" error={errors.desc} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <span style={{ fontSize: 11, color: form.desc.length >= 50 ? G : "#9ca3af" }}>
                {form.desc.length} / 50 min.
              </span>
            </div>

            <div style={{ marginTop: 20, background: G_LIGHT, borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: G_DARK, marginBottom: 11, display: "flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={13} /> Conseils pour une bonne description
              </div>
              {[
                "Mentionnez l'emplacement et les attractions proches",
                "Décrivez les services inclus (piscine, restaurant…)",
                "Parlez de l'ambiance et du type de clientèle",
                "Indiquez ce qui vous différencie des autres établissements",
              ].map((tip, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                  <Check size={12} color={G} style={{ marginTop: 3, flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, color: "#374151" }}>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 — Équipements (catégories partagées) ── */}
        {step === 3 && (
          <div className="rh-card" style={cardStyle}>
            <h2 style={h2Style}>Équipements & services</h2>
            <p style={subStyle}>Sélectionnez ce qui est disponible. Vous pourrez tout affiner plus tard depuis votre espace.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 22, marginBottom: 28 }}>
              {/* 🆕 on boucle sur les CATEGORIES partagées, et on regroupe les SUGGESTED_EQUIPMENTS par catégorie */}
              {CATEGORIES.filter(c => c.id !== "autre").map(cat => {
                const items = SUGGESTED_EQUIPMENTS.filter(e => e.category === cat.id);
                if (!items.length) return null;
                const { Icon } = cat;
                return (
                  <div key={cat.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 11 }}>
                      <Icon size={15} color={G_DARK} />
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: INK, letterSpacing: "0.01em" }}>{cat.label}</span>
                    </div>
                    <div className="rh-equip">
                      {items.map(eq => {
                        const sel = isEquip(eq.name);
                        return (
                          <div key={eq.name} onClick={() => toggleEquip(eq.name, cat.id)}
                            style={{
                              display: "flex", alignItems: "center", gap: 11, padding: "11px 14px",
                              border: `1.5px solid ${sel ? G : "#eef0f1"}`, borderRadius: 11, cursor: "pointer",
                              background: sel ? G_LIGHT : "#fff", transition: "all .15s",
                            }}>
                            <span style={{ fontSize: 13, fontWeight: 500, color: sel ? G_DARK : "#374151", flex: 1 }}>{eq.name}</span>
                            <div style={{
                              width: 19, height: 19, borderRadius: 6, flexShrink: 0,
                              border: `1.5px solid ${sel ? G : "#d1d5db"}`, background: sel ? G : "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s",
                            }}>
                              {sel && <Check size={11} color="#fff" strokeWidth={3} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Récap */}
            <div style={{ background: "#f7f8f7", borderRadius: 14, padding: "20px 22px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 14 }}>Récapitulatif</div>
              <div className="rh-recap">
                {[
                  { label: "Type",        val: TYPES.find(t => t.value === form.type)?.label || "—" },
                  { label: "Île",         val: form.island || "—" },
                  { label: "Nom",         val: form.name || "—" },
                  { label: "Ville",       val: form.city || "—" },
                  { label: "Équipements", val: `${form.equipments.length} sélectionné${form.equipments.length > 1 ? "s" : ""}` },
                ].map((r, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3 }}>{r.label}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{r.val}</div>
                  </div>
                ))}
              </div>
            </div>

            {errors.api && (
              <div style={{ marginTop: 16, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 11, padding: "12px 15px", fontSize: 13, color: "#dc2626", display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={15} style={{ flexShrink: 0 }} /> {errors.api}
              </div>
            )}
          </div>
        )}

        {/* ── ÉTAPE 4 — Confirmation ── */}
        {step === 4 && (
          <div className="rh-card" style={{ ...cardStyle, padding: "52px 32px", textAlign: "center" }}>
            <div style={{ width: 74, height: 74, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
              <Check size={34} color={G} strokeWidth={2.5} />
            </div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 27, fontWeight: 700, color: INK, marginBottom: 11, letterSpacing: "-0.01em" }}>
              Demande envoyée !
            </h2>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.75, maxWidth: 450, margin: "0 auto 32px" }}>
              Votre demande a bien été reçue. Notre équipe l'examine et vous contactera à{" "}
              <strong style={{ color: INK }}>{form.email}</strong> sous 48 heures pour activer votre espace.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => navigate("/")}
                style={{ background: G, color: "#fff", border: "none", borderRadius: 12, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
                Retour à l'accueil
              </button>
              <button onClick={() => { setStep(0); setForm({ type: "", island: "", name: "", city: "", adress: "", nif: "", email: "", phone: "", desc: "", equipments: [] }); }}
                style={{ background: G_LIGHT, color: G_DARK, border: "none", borderRadius: 12, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Soumettre un autre établissement
              </button>
            </div>
          </div>
        )}

        {/* ── Navigation ── */}
        {step < 4 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 22 }}>
            <button onClick={step === 0 ? () => navigate("/") : prev}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #e5e7eb", borderRadius: 11, padding: "11px 18px", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
              <ChevronLeft size={16} /> {step === 0 ? "Annuler" : "Retour"}
            </button>

            {step < 3 ? (
              <button onClick={next}
                style={{ display: "flex", alignItems: "center", gap: 6, background: G, color: "#fff", border: "none", borderRadius: 11, padding: "11px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "background .18s" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
                Continuer <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading}
                style={{ display: "flex", alignItems: "center", gap: 6, background: loading ? "#cbd5cb" : G, color: "#fff", border: "none", borderRadius: 11, padding: "11px 24px", fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", transition: "background .18s" }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = G_DARK; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.backgroundColor = G; }}>
                {loading ? "Envoi…" : <><Check size={15} /> Soumettre la demande</>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}